/**
 * Writing a value THROUGH a scope.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The write lands on the MOST SPECIFIC selected level: pick a country and you
 * edit that country; pick a campaign inside it and you edit the campaign. Every
 * function returns a COMPLETE condition with all other levels byte identical —
 * the modal submits the whole conditions array, so a careless spread here would
 * silently delete another country's override on save.
 */

import type { TagRuleCampaignThreshold, TagRuleCondition } from '@/hooks';

import { normScope, scopeTarget, type TagRuleScope } from './scope';

function writeCountry(
  c: TagRuleCondition,
  countryKey: string,
  value: number,
): TagRuleCondition {
  const key = normScope(countryKey);
  const rows = c.countryThresholds ?? [];
  // Writing a number pins this scope — a comparison the row carried is
  // replaced, not kept alongside.
  const next = rows.some((t) => normScope(t.countryKey) === key)
    ? rows.map((t) => {
        if (normScope(t.countryKey) !== key) return t;
        const { compareTo: _ct, ...rest } = t;
        return { ...rest, value };
      })
    : [...rows, { countryKey: key, value }];
  return { ...c, countryThresholds: next };
}

function writeCampaign(
  c: TagRuleCondition,
  scope: TagRuleScope,
  value: number,
): TagRuleCondition {
  const rows = c.campaignThresholds ?? [];
  const hit = (t: TagRuleCampaignThreshold): boolean =>
    t.provider === scope.provider && normScope(t.campaignId) === normScope(scope.campaignId!);
  const next = rows.some(hit)
    ? rows.map((t) => {
        if (!hit(t)) return t;
        const { compareTo: _ct, ...rest } = t;
        return { ...rest, value };
      })
    : [...rows, { provider: scope.provider!, campaignId: scope.campaignId!, value }];
  return { ...c, campaignThresholds: next };
}

/** Write `value` at the selected level, creating the row if it doesn't exist. */
export function setValueForScope(
  c: TagRuleCondition,
  scope: TagRuleScope,
  value: number,
): TagRuleCondition {
  const target = scopeTarget(scope);
  if (target === 'campaign') return writeCampaign(c, scope, value);
  if (target === 'country') return writeCountry(c, scope.countryKey!, value);
  return { ...c, value };
}

/**
 * Drop the selected level's override so it inherits again. An emptied list
 * becomes `undefined`, matching how the condition editor already normalises so
 * the saved JSON doesn't accumulate empty arrays.
 */
export function clearValueForScope(
  c: TagRuleCondition,
  scope: TagRuleScope,
): TagRuleCondition {
  const target = scopeTarget(scope);
  if (target === 'campaign') {
    const next = (c.campaignThresholds ?? []).filter(
      (t) =>
        !(
          t.provider === scope.provider &&
          normScope(t.campaignId) === normScope(scope.campaignId!)
        ),
    );
    return { ...c, campaignThresholds: next.length ? next : undefined };
  }
  if (target === 'country') {
    const key = normScope(scope.countryKey!);
    const next = (c.countryThresholds ?? []).filter(
      (t) => normScope(t.countryKey) !== key,
    );
    return { ...c, countryThresholds: next.length ? next : undefined };
  }
  return c;
}
